// packages/chatbot/src/hooks/controller/useHistoryStrategy.ts

import { useMemo, useCallback } from 'react';
import { useConversations } from '@dopomogai/agent-kit';
import { visitorConversationsService } from '@dopomogai/api-client';
import type { Message } from '@dopomogai/types';
import type { ConversationSummary, UIMessage } from '../../types';
import { useVisitorHistory } from '../useVisitorHistory';
import { processBackendMessagesToUI } from '../../utils/messages';
import { getVisitorId } from '../../utils/storage';
import { getWidFromWat } from '../../utils/widget';

type Params = {
  isWidget: boolean;
  activeAgentId: string | null;
  apiBaseUrl?: string | null;
  wat?: string | null;
  loadHistory: (conversationId: string) => void;
};

type HistoryList = {
  items: ConversationSummary[];
  isLoading: boolean;
  error: any;
  isMoreLoading: boolean;
  hasMore: boolean;
  loadMore: () => void;
  refresh: () => void;
};

export type HistoryStrategy = {
  list: HistoryList;
  loadFullTranscript: (conversationId: string) => Promise<UIMessage[] | null>;
};

export function useHistoryStrategy({
  isWidget,
  activeAgentId,
  apiBaseUrl,
  wat,
  loadHistory,
}: Params): HistoryStrategy {
  const { conversations, isLoading: isTestLoading, error: testError } = useConversations(
    isWidget ? null : activeAgentId
  );

  const visitor = useVisitorHistory({
    enabled: isWidget && Boolean(apiBaseUrl && wat),
    apiBaseUrl: apiBaseUrl,
    wat,
    getVisitorId,
    getWid: getWidFromWat,
  });

  const list: HistoryList = useMemo(() => {
    if (isWidget) {
      return {
        items: (visitor.history.items || []) as ConversationSummary[],
        isLoading: visitor.isLoading,
        error: visitor.error,
        isMoreLoading: false,
        hasMore: false,
        loadMore: () => {},
        refresh: visitor.refresh,
      };
    }
    return {
      items: conversations as ConversationSummary[],
      isLoading: isTestLoading,
      error: testError,
      isMoreLoading: false,
      hasMore: false,
      loadMore: () => {},
      refresh: () => {},
    };
  }, [
    isWidget,
    visitor.history.items,
    visitor.isLoading,
    visitor.error,
    visitor.refresh,
    conversations,
    isTestLoading,
    testError,
  ]);

  // Memoize loader to keep a stable identity
  const loadFullTranscript = useCallback(async (conversationId: string): Promise<UIMessage[] | null> => {
    if (!isWidget) {
      loadHistory(conversationId);
      return null;
    }

    if (!apiBaseUrl || !wat) return null;
    const wid = getWidFromWat(wat);
    const visitorId = getVisitorId();
    if (!wid || !visitorId) return null;

    const conv = await visitorConversationsService.getOneForVisitor(
      apiBaseUrl,
      wid as any,
      conversationId,
      visitorId,
      wat
    );

    if ((conv as any)?.isError) {
      console.warn('[useHistoryStrategy] Failed to load visitor conversation:', conv);
      return null;
    }

    const msgs = Array.isArray((conv as any)?.messages) ? ((conv as any).messages as Message[]) : [];
    return processBackendMessagesToUI(msgs);
  }, [isWidget, apiBaseUrl, wat, loadHistory]);

  // Return a stable strategy object so effects can depend on specific members
  return useMemo(() => ({ list, loadFullTranscript }), [list, loadFullTranscript]);
}
